import { DocCategories } from "@/data/enums/docs";
import { DataModules } from "@/store/modules/data/modules";
import _ from "@/boot/lodash";
import store from "@/store";
import { RegexMatch, AdminRoutes } from "@/data/enums/router";
import { deSerialisedConfig } from "@/helpers/table";
import type { Location, Route } from "vue-router";
import type { CreateElement } from "vue";
import DefaultFilters from "@/data/filters/userGroups";
import DefaultSorters from "@/data/sorters/userGroups";
import { OrgFeatureKeys, TemplateTypes } from "@upmind-automation/types";

// Components
import AdminPromotionsView from "@/views/admin/settings/promotions/list/index.vue";
import PromotionsListing from "@/components/app/admin/catalogue/promotions/promotionsListing.vue";

// Menus
import { AdminPromotionsRoutes } from "@/router/admin/settings/menu";

// Sorters
import {
  ActiveSorter,
  DefaultPromotionsSorters
} from "@/data/sorters/promotions";
import { DefaultOrderTemplatesSorters } from "@/data/sorters/orderTemplates";

// Filters
import {
  IsActiveParam,
  IsInactiveParam,
  IsDeletedPromotionParam,
  ActiveFilter,
  DefaultPromotionsFilters
} from "@/data/filters/promotions";
import { DefaultOrderTemplatesFilters } from "@/data/filters/orderTemplates";

import EmailsHistoryProvider from "@/components/app/global/emailHistory/emailsHistoryProvider.vue";
import EmailHistoryTable from "@/components/app/global/emailHistory/emailHistoryTable.vue";
import { EmailTypes } from "@/data/enums/emails";
import {
  IsSentParam,
  IsBouncedParam,
  IsFailedParam
} from "@/data/filters/emails";

import TicketsListing from "@/components/app/global/tickets/ticketsListing.vue";
import {
  IsClosedParam,
  IsActiveParam as IsActiveTicketParam,
  IsUserParam,
  IsLeadUserParam,
  IsNotLeadUserParam
} from "@/data/filters/tickets";
import type { ITicket } from "@upmind-automation/types";

const renderEmailHistory = ({ manualFilters = {} }) => ({
  render(h: CreateElement) {
    return h(EmailsHistoryProvider, {
      props: {
        emailType: EmailTypes.USER,
        emailTypeId: (this as any).$route.params.userId,
        manualFilters: manualFilters || {}
      },
      scopedSlots: {
        default: () =>
          h(EmailHistoryTable, {
            props: {
              rowProps: email => ({
                to: { name: "adminUserEmailLog", params: { emailId: email.id } }
              })
            }
          })
      }
    });
  }
});

const openUserGroupModal = (options: {
  viewRouteName: string;
  userGroupCode?: string;
  params: any;
}) => {
  store.dispatch(`data/${DataModules.USER_GROUPS}/openAddEditModal`, {
    props: {
      viewRoute: () => ({
        name: options.viewRouteName,
        params: options.params
      }),
      userGroupCode: options.userGroupCode
    }
  });
};

export default [
  {
    path: "settings",
    components: {
      default: {
        name: "AdminSettings",
        template: `<router-view />`
      }
    },
    children: [
      {
        path: "",
        name: "adminSettings",
        component: () => import("@/views/admin/settings/index.vue"),
        props: {
          routes: require("./menu").routes
        },
        meta: {
          backTo: "_.settings",
          title: "_.settings",
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: ["configuration-notes"]
        }
      },
      {
        name: "brandGeneralSettings",
        path: "general",
        meta: {
          auth: ["get_brand"],
          title: "_.general_settings",
          requiresBrand: true,
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: ["branding"]
        },
        props: true,
        component: () => import("@/views/admin/settings/brand/index.vue")
      },
      {
        name: "brandClientsSettings",
        path: "clients",
        meta: {
          auth: [],
          title: "_.client_settings",
          requiresBrand: true
        },
        props: {
          dynamicShowOnOrg: false,
          predefinedShowOnOrg: false,
          settingsCode: "clients"
        },
        component: () => import("@/views/admin/settings/dynamic/index.vue")
      },
      {
        name: "brandStoreSettings",
        path: "store",
        props: true,
        component: () =>
          import("@/views/admin/settings/ecommerce/store/index.vue"),
        meta: {
          auth: ["get_brand"],
          title: "_.business_settings",
          requiresBrand: true
        }
      },
      {
        path: "payment-providers",
        name: "brandPaymentProvidersSettings",
        component: () =>
          import("@/views/admin/settings/paymentProviders/index.vue"),
        props: true,
        meta: {
          auth: ["get_brand", "list_brand_gateways"],
          title: "_.payment_providers_settings",
          requiresBrand: true
        },
        children: [
          {
            path: RegexMatch.NEW,
            name: "brandPaymentProvidersSettingsOpenModal",
            component: () =>
              import(
                "@/components/app/admin/settings/payments/openAddEditGatewayModal.vue"
              )
          }
        ]
      },
      {
        path: "terms-and-conditions",
        name: AdminRoutes.SETTINGS_TERMS_AND_CONDITIONS,
        component: () =>
          import("@/views/admin/settings/legal/termsAndConditions/index.vue"),
        meta: {
          auth: ["get_brand", "get_current_terms_and_conditions"],
          title: "_.terms_and_conditions",
          requiresBrand: true,
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: ["other-setup-items.how-to-create-terms-and-conditions"]
        }
      },
      {
        name: AdminRoutes.CUSTOM_CLIENT_FIELDS_SETTINGS,
        path: "custom-client-fields",
        props: true,
        component: () =>
          import("@/views/admin/settings/customClientFields/index.vue"),
        meta: {
          auth: ["list_client_fields"],
          title: "_.custom_fields",
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: [
            "configuration-notes",
            "branding",
            "branding.how-to-configure-your-brand"
          ],
          requiresBrand: true
        }
      },
      {
        name: AdminRoutes.CUSTOM_BASKET_FIELDS_SETTINGS,
        path: "custom-basket-fields",
        props: true,
        component: () =>
          import("@/views/admin/settings/customBasketFields/index.vue"),
        meta: {
          auth: ["list_basket_fields"],
          title: "_.basket_custom_fields",
          requiresBrand: true
        }
      },
      {
        name: AdminRoutes.CUSTOM_CANCELLATION_FIELDS_SETTINGS,
        path: "custom-cancellation-fields",
        props: true,
        component: () =>
          import("@/views/admin/settings/customFields/index.vue"),
        meta: {
          auth: [],
          title: "_.custom_cancellation_fields",
          requiresBrand: true
        }
      },
      {
        name: "orderTemplatesSettings",
        path: "order-templates",
        component: () =>
          import("@/views/admin/settings/orderTemplates/index.vue"),
        props: {
          availableSorters: DefaultOrderTemplatesSorters,
          availableFilters: DefaultOrderTemplatesFilters
        },
        meta: {
          auth: [],
          title: "_.order_templates",
          requiresBrand: true
        }
      },
      {
        name: AdminRoutes.PDF_TEMPLATES_SETTINGS,
        path: "pdf-templates",
        component: () =>
          import("@/views/admin/settings/pdfTemplates/index.vue"),
        meta: {
          auth: [],
          title: "_.pdf_templates",
          requiresBrand: true
        }
      },
      {
        path: `pdf-templates/:templateCode`,
        name: AdminRoutes.PDF_TEMPLATES_EDIT,
        meta: {
          auth: []
        },
        component: () =>
          import("@/views/admin/settings/pdfTemplates/template/index.vue"),
        props: (to: Route) => {
          return {
            templateCode: to.params.templateCode
          };
        }
      },
      {
        path: "domains",
        name: "brandDomainsSettings",
        component: () => import("@/views/admin/settings/domains/index.vue"),
        meta: {
          auth: ["list_oauth_clients"],
          title: "_.domains",
          backTo: "_.domains",
          requiresBrand: true,
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: ["branding.how-to-add-a-unique-domain-name"]
        }
      },
      {
        path: "currencies",
        name: "brandCurrenciesSettings",
        meta: {
          auth: ["get_brand"],
          title: "_.currencies",
          requiresBrand: false,
          categorySlug: DocCategories.CURRENCIES,
          docsSlugs: ["currencies"]
        },
        props: {
          settingsCode: "currencies",
          dynamicShowOnOrg: false
        },
        component: () => import("@/views/admin/settings/dynamic/index.vue")
      },
      {
        path: "security",
        name: "brandSecuritySettings",
        meta: {
          auth: ["get_brand"],
          title: "_.security",
          requiresBrand: false,
          categorySlug: DocCategories.SECURITY,
          docsSlugs: ["security"]
        },
        props: {
          settingsCode: "security",
          dynamicShowOnOrg: false
        },
        component: () => import("@/views/admin/settings/dynamic/index.vue")
      },
      {
        path: "spam-conditions",
        name: "admin__settingsSpamConditions",
        meta: {
          auth: [],
          title: "_.spam_conditions",
          backTo: "_.spam_conditions",
          requiresBrand: true
        },
        component: () =>
          import("@/views/admin/settings/spamConditions/index.vue")
      },
      {
        path: "imports",
        component: {
          name: "AdminImportsWrapper",
          template: `<router-view />`
        },
        children: [
          {
            path: "",
            name: "adminImports",
            component: () => import("@/views/admin/settings/imports/index.vue"),
            meta: {
              auth: ["list_imports"],
              title: "_.imports",
              backTo: "_.imports",
              requiresBrand: true,
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["importing-data"]
            }
          },
          {
            path: `:importId(${RegexMatch.UUID})`,
            component: () =>
              import("@/views/admin/settings/imports/import/index.vue"),
            props: {
              routes: require("./menu").importTabigation
            },
            meta: {
              auth: ["get_import"],
              requiresBrand: true
            },
            children: [
              {
                path: "",
                redirect: "overview"
              },
              {
                path: "overview",
                name: "adminImport",
                component: () =>
                  import(
                    "@/views/admin/settings/imports/import/overview/index.vue"
                  ),
                meta: {
                  title: "_.import_overview",
                  backTo: "_.import_overview"
                }
              },
              {
                path: "logs",
                name: "adminImportLogs",
                component: () =>
                  import(
                    "@/views/admin/settings/imports/import/logs/index.vue"
                  ),
                meta: {
                  title: "_.import_logs",
                  backTo: "_.import_logs"
                }
              }
            ]
          }
        ]
      },
      {
        path: "client-templates",
        component: () =>
          import("@/views/admin/settings/clientTemplates/index.vue"),
        props: {
          routes: require("./menu").ClientTemplatesTabigation
        },
        meta: {
          title: "_.client_area_templates",
          backTo: "_.client_area_templates",
          requiresBrand: true
        },
        children: [
          {
            path: "",
            name: "admin__clientTemplates",
            redirect: "slots"
          },
          {
            path: "slots",
            name: "admin__clientTemplateSlots",
            component: () =>
              import("@/views/admin/settings/clientTemplates/slots/index.vue"),
            auth: ["list_client_area_template_slots"]
          },
          {
            path: "templates",
            name: "admin__clientTemplateTemplates",
            component: () =>
              import(
                "@/views/admin/settings/clientTemplates/templates/index.vue"
              ),
            auth: ["list_templates"]
          }
        ]
      },
      {
        path: "email-templates",
        component: {
          name: AdminRoutes.EMAIL_TEMPLATES_SETTINGS,
          template: `<router-view :key="$route.path" />`
        },
        meta: {
          requiresBrand: true
        },
        children: [
          {
            path: "",
            name: "adminEmailTemplates",
            component: () =>
              import("@/views/admin/settings/emailTemplates/index.vue"),
            meta: {
              auth: ["list_notification_channels", "list_templates"],
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["email-messages"]
            }
          },
          {
            path: "add",
            name: AdminRoutes.EMAIL_TEMPLATE_ADD,
            meta: {
              auth: [
                "list_notification_channels",
                "list_templates",
                "create_template"
              ]
            },
            component: () =>
              import(
                "@/views/admin/settings/emailTemplates/emailTemplate/add/index.vue"
              )
          },
          {
            path: `:templateId(${RegexMatch.UUID})`,
            name: AdminRoutes.EMAIL_TEMPLATE_SETTINGS,
            redirect: { name: AdminRoutes.EMAIL_TEMPLATE_EDIT },
            meta: {
              auth: [
                "list_notification_channels",
                "list_templates",
                "get_template"
              ]
            },
            component: () =>
              import(
                "@/views/admin/settings/emailTemplates/emailTemplate/index.vue"
              ),
            props: (to: Route) => {
              return {
                routes: require("./menu").emailTemplatesTabigations,
                templateId: to.params.templateId
              };
            },
            children: [
              {
                path: `edit`,
                name: AdminRoutes.EMAIL_TEMPLATE_EDIT,
                component: () =>
                  import(
                    "@/views/admin/settings/emailTemplates/emailTemplate/edit/index.vue"
                  )
              },
              {
                path: `email-history`,
                name: AdminRoutes.EMAIL_TEMPLATE_HISTORY,
                component: () =>
                  import(
                    "@/views/admin/settings/automation/actions/action/emailHistory/index.vue"
                  )
              }
            ]
          }
        ]
      },
      {
        path: "email-layouts",
        redirect: { name: AdminRoutes.EMAIL_LAYOUTS_ADD },
        component: {
          name: AdminRoutes.EMAIL_LAYOUTS,
          template: `<router-view :key="$route.path" />`
        },
        meta: {
          requiresBrand: true
        },
        children: [
          {
            path: "add",
            name: AdminRoutes.EMAIL_LAYOUTS_ADD,
            meta: {
              auth: [
                "list_notification_channels",
                "list_templates",
                "create_template"
              ]
            },
            component: () =>
              import(
                "@/views/admin/settings/emailTemplates/templateLayout/add/index.vue"
              ),
            props: () => {
              return {
                type: TemplateTypes.NOTIFICATION_LAYOUT
              };
            }
          },
          {
            path: `:layoutId(${RegexMatch.UUID})`,
            name: AdminRoutes.EMAIL_LAYOUTS_EDIT,
            meta: {
              auth: [
                "list_notification_channels",
                "list_templates",
                "get_template"
              ]
            },
            component: () =>
              import(
                "@/views/admin/settings/emailTemplates/templateLayout/edit/index.vue"
              ),
            props: (to: Route) => {
              return {
                layoutId: to.params.layoutId
              };
            }
          }
        ]
      },
      {
        name: AdminRoutes.SUPPORT_DEPARTMENTS,
        path: "support-departments",
        props: true,
        component: () =>
          import("@/views/admin/settings/ticketDepartments/index.vue"),
        meta: {
          auth: [],
          title: "_.support_departments",
          backTo: "_.support_departments"
        }
      },
      {
        name: "adminCataloguePriceLists",
        path: "price-lists",
        components: {
          default: () => import("@/views/admin/settings/priceLists/index.vue")
        },
        meta: {
          auth: ["list_pricelists"],
          title: "_.price_lists",
          backTo: "_.price_lists"
        }
      },
      {
        name: "adminCatalogueProvisioning",
        path: "provider-configurations",
        components: {
          default: () => import("@/views/admin/settings/provisioning/index.vue")
        },
        meta: {
          auth: [],
          title: "_.provision_configurations",
          backTo: "_.provision_configurations",
          requiresBrand: false
        }
      },
      {
        name: "adminBrandProvisioning",
        path: "brand-provisioning",
        components: {
          default: () =>
            import("@/views/admin/settings/brandProvisioning/index.vue")
        },
        meta: {
          auth: [],
          title: "_.brand_provisioning_fields",
          backTo: "_.brand_provisioning_fields",
          requiresBrand: true
        },
        children: [
          {
            path: `:categoryId(${RegexMatch.UUID})`,
            name: "adminBrandProvisioningPreview",
            beforeEnter: (to, from, next) => {
              next({ name: "adminBrandProvisioning" });
              store.dispatch("ui/endRouting");
              store.dispatch(
                `data/${DataModules.PROVISIONING_BRAND}/openManageBrandFieldsModal`,
                {
                  blueprintId: _.get(to.params, "categoryId")
                }
              );
            }
          }
        ]
      },
      {
        path: "automation/webhooks",
        name: AdminRoutes.WEBHOOKS_SETTINGS,
        component: () =>
          import("@/views/admin/settings/automation/webhooks/index.vue"),
        meta: {
          auth: ["list_webhooks"],
          title: "_.webhooks",
          backTo: "_.webhooks",
          requiresBrand: true,
          requiredFeatureKeys: [OrgFeatureKeys.WEBHOOKS]
        }
      },
      {
        path: `automation/webhooks/:webhookId(${RegexMatch.UUID})`,
        name: AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK,
        redirect: { name: AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK_EDIT },
        component: () =>
          import(
            "@/views/admin/settings/automation/webhooks/webhook/index.vue"
          ),
        props: (to: Route) => {
          return {
            routes: require("./menu").automationWebhookTabigations,
            webhookId: to.params.webhookId
          };
        },
        meta: {
          requiresBrand: true,
          auth: ["get_template"],
          requiredFeatureKeys: [OrgFeatureKeys.WEBHOOKS]
        },
        children: [
          {
            path: "edit",
            name: AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK_EDIT,
            component: () =>
              import(
                "@/views/admin/settings/automation/webhooks/webhook/edit/index.vue"
              ),
            meta: {
              title: "_.webhook_overview",
              backTo: "_.webhook_overview"
            }
          },
          {
            path: "events",
            name: AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK_EVENTS,
            component: () =>
              import(
                "@/views/admin/settings/automation/webhooks/webhook/events/index.vue"
              ),
            meta: {
              title: "_.webhook_events",
              backTo: "_.webhook_events"
            }
          }
        ]
      },
      {
        path: `automation/triggers/:hookId(${RegexMatch.UUID})`,
        name: "adminAutomationHook",
        component: () =>
          import(
            "@/views/admin/settings/automation/triggers/trigger/index.vue"
          ),
        meta: {
          auth: ["get_notifiable_hook", "list_templates"],
          requiresBrand: true
        }
      },
      {
        path: "automation/actions/add",
        component: () =>
          import("@/views/admin/settings/automation/actions/action/index.vue"),
        redirect: { name: "adminAutomationAddAction" },
        meta: {
          requiresBrand: true
        },
        props: (to: Route) => {
          const config = deSerialisedConfig(
            (to.query["AAT"] as string) || "",
            []
          );
          const channelCode = config?.constFilters?.find(
            i => i.key === "channel.code"
          )?.value;

          return {
            channelCode,
            isCreate: true
          };
        },
        children: [
          {
            path: ``,
            name: "adminAutomationAddAction",
            component: () =>
              import(
                "@/views/admin/settings/automation/actions/action/editAction/index.vue"
              ),

            meta: {
              auth: ["create_template"]
            }
          }
        ]
      },
      {
        path: `automation/actions/:templateId(${RegexMatch.UUID})`,
        name: "adminAutomationAction",
        redirect: { name: "adminEditAutomationAction" },
        component: () =>
          import("@/views/admin/settings/automation/actions/action/index.vue"),
        props: (to: Route) => {
          return {
            routes: require("./menu").automationActionTabigations,
            templateId: to.params.templateId,
            channelCode: _.get(to.query, "channel.code")
          };
        },
        meta: {
          requiresBrand: true,
          auth: ["get_template"]
        },
        children: [
          {
            path: `edit`,
            name: "adminEditAutomationAction",
            component: () =>
              import(
                "@/views/admin/settings/automation/actions/action/editAction/index.vue"
              )
          },
          {
            path: `email-history`,
            name: "adminAutomationActionEmailHistory",
            component: () =>
              import(
                "@/views/admin/settings/automation/actions/action/emailHistory/index.vue"
              )
          }
        ]
      },
      {
        path: "automation",
        name: "adminAutomation",
        redirect: { name: "adminAutomationChannels" },
        component: () => import("@/views/admin/settings/automation/index.vue"),
        props: {
          routes: require("./menu").automationRoutes
        },
        meta: {
          requiresBrand: true
        },
        children: [
          {
            path: "channels",
            component: {
              name: "AdminSettingsAutomationChannels",
              template: `<router-view :key="$route.path" />`
            },
            children: [
              {
                path: "",
                meta: {
                  auth: ["list_notification_channels", "list_templates"],
                  categorySlug: DocCategories.SETUP_CONFIGURATION,
                  docsSlugs: ["notifications"]
                },
                name: "adminAutomationChannels",
                component: () =>
                  import("@/views/admin/settings/automation/channels/index.vue")
              }
            ]
          },
          {
            path: "triggers",
            name: "adminAutomationTriggers",
            component: () =>
              import("@/views/admin/settings/automation/triggers/index.vue"),
            meta: {
              auth: ["list_notifiable_hooks"],
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["notifications"]
            }
          }
        ]
      },
      {
        path: `users/:userId(${RegexMatch.UUID})`,
        props: true,
        component: {
          name: "AdminUser",
          props: ["userId"],
          template: "<router-view :key='`user_${userId}`' />"
        },
        meta: {
          title: "_.user"
        },
        children: [
          {
            path: "",
            name: "adminUser",
            props: route => ({
              userId: route.params.userId,
              routes: require("./menu").userRoutes
            }),
            redirect: { name: "adminUserOverview" },
            component: () =>
              import("@/views/admin/settings/users/user/index.vue"),
            children: [
              {
                path: "overview",
                name: "adminUserOverview",
                component: () =>
                  import(
                    "@/views/admin/settings/users/user/overview/index.vue"
                  ),
                meta: {
                  title: "_.overview",
                  backTo: "_.overview"
                }
              },
              {
                path: "tickets",
                component: {
                  name: "AdminUserTicketsWrapper",
                  template: `<router-view v-bind="$attrs" />`
                },
                children: [
                  {
                    path: "",
                    name: "adminUserSupportTickets",
                    component: () =>
                      import(
                        "@/views/admin/settings/users/user/tickets/index.vue"
                      ),
                    meta: {
                      title: "_.tickets",
                      breadcrumb: {
                        label: "_action.edit"
                      }
                    },
                    redirect: { name: "admin__userAssignedTickets" },
                    props: (route: Route) => ({
                      routes: require("./menu").usersTicketsTabigation,
                      userId: route.params.userId
                    }),
                    children: [
                      {
                        path: "assigned",
                        name: "admin__userAssignedTickets",
                        component: TicketsListing,
                        props: (route: Route) => ({
                          routeFilters: () => ({
                            ...IsLeadUserParam(route.params),
                            ...IsActiveTicketParam()
                          }),
                          ticketRoute: (ticket: ITicket) => ({
                            name: AdminRoutes.SUPPORT_TICKET,
                            params: {
                              ticketId: ticket.id
                            }
                          }),
                          showStaged: true
                        }),
                        meta: {
                          backTo: "_.assigned_tickets",
                          title: "_.assigned_tickets"
                        }
                      },
                      {
                        path: "watched",
                        name: "admin__userWatchedTickets",
                        component: TicketsListing,
                        props: (route: Route) => ({
                          routeFilters: () => ({
                            ...IsUserParam(route.params),
                            ...IsNotLeadUserParam(route.params),
                            ...IsActiveTicketParam()
                          }),
                          ticketRoute: (ticket: ITicket) => ({
                            name: AdminRoutes.SUPPORT_TICKET,
                            params: {
                              ticketId: ticket.id
                            }
                          }),
                          showStaged: true
                        }),
                        meta: {
                          backTo: "_.watched_tickets",
                          title: "_.watched_tickets"
                        }
                      },
                      {
                        path: "closed",
                        name: "admin__userClosedTickets",
                        component: TicketsListing,
                        props: (route: Route) => ({
                          routeFilters: () => ({
                            ...IsUserParam(route.params),
                            ...IsClosedParam()
                          }),
                          ticketRoute: (ticket: ITicket) => ({
                            name: AdminRoutes.SUPPORT_TICKET,
                            params: {
                              ticketId: ticket.id
                            }
                          }),
                          showStaged: true
                        }),
                        meta: {
                          backTo: "_.closed_tickets",
                          title: "_.closed_tickets"
                        }
                      }
                    ]
                  }
                ]
              },
              {
                path: "email-history",
                name: "adminUserEmailHistory",
                component: () =>
                  import(
                    "@/views/admin/settings/users/user/email-history/index.vue"
                  ),
                meta: {
                  title: "_.email_history",
                  backTo: "_.email_history"
                },
                redirect: { name: "adminUserEmailHistoryAll" },
                children: [
                  {
                    path: "failed",
                    name: "adminUserEmailHistoryFailed",
                    component: renderEmailHistory({
                      manualFilters: {
                        ...IsFailedParam()
                      }
                    })
                  },
                  {
                    path: "bounced",
                    name: "adminUserEmailHistoryBounced",
                    component: renderEmailHistory({
                      manualFilters: {
                        ...IsBouncedParam()
                      }
                    })
                  },
                  {
                    path: "sent",
                    name: "adminUserEmailHistorySent",
                    component: renderEmailHistory({
                      manualFilters: {
                        ...IsSentParam()
                      }
                    })
                  },
                  {
                    path: "all",
                    name: "adminUserEmailHistoryAll",
                    component: renderEmailHistory({}),
                    children: [
                      {
                        path: `:emailId(${RegexMatch.UUID})`,
                        name: "adminUserEmailLog",
                        beforeEnter: async (
                          to: Route,
                          from: Route,
                          next: (
                            options: boolean | Location | Location["path"]
                          ) => void
                        ) => {
                          const { emailId } = to.params;
                          if (emailId) {
                            store.dispatch("ui/open/slideModal", {
                              config: {
                                component: () =>
                                  import(
                                    "@/components/app/global/emailHistory/viewEmailModal.vue"
                                  ),
                                canCancel: ["button"],
                                props: {
                                  viewRoute: () => ({
                                    name: "adminUserEmailLog",
                                    params: to.params
                                  }),
                                  emailId
                                }
                              }
                            });
                          }
                          store.dispatch("ui/endRouting");
                          return next(!from.matched.length); // Important to maintain pagination/filters etc on parent list view
                        }
                      }
                    ]
                  }
                ]
              },
              {
                path: "activity-log",
                name: "adminUserActivityLog",
                component: () =>
                  import(
                    "@/views/admin/settings/users/user/activityLog/index.vue"
                  ),
                meta: {
                  title: "_.activity_log",
                  backTo: "_.activity_log"
                }
              },
              {
                path: "notifications",
                name: "adminUserNotifications",
                component: () =>
                  import(
                    "@/views/admin/settings/users/user/notifications/index.vue"
                  ),
                meta: {
                  title: "_.notifications",
                  backTo: "_.notifications"
                }
              }
            ]
          }
        ]
      },
      {
        path: "users",
        redirect: { name: "adminUsers" },
        component: () => import("@/views/admin/settings/users/index.vue"),
        props: {
          routes: require("./menu").usersTabigation
        },
        meta: {
          auth: ["list_users"]
        },
        children: [
          {
            path: "all",
            name: "adminUsers",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: { id: "UT" },
            meta: {
              title: "_.all_staff_users",
              backTo: "_.all_staff_users",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          },
          {
            path: "active",
            name: "adminUsersActive",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: { id: "UT", active: true },
            meta: {
              title: "_.active_staff_users",
              backTo: "_.active_staff_users",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          },
          {
            path: "inactive",
            name: "adminUsersInactive",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: { id: "UT", active: false },
            meta: {
              title: "_.inactive_staff_users",
              backTo: "_.inactive_staff_users",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          }
        ]
      },
      {
        path: "user-groups",
        component: {
          name: "AdminSettingsUserGroups",
          template: `<router-view :key="$route.path" />`
        },
        meta: {
          auth: ["list_user_groups"]
        },
        children: [
          {
            path: "",
            name: "listUserGroups",
            component: () =>
              import("@/views/admin/settings/userGroups/index.vue"),
            props: () => ({
              availableFilters: () => DefaultFilters(),
              availableSorters: () => DefaultSorters()
            }),
            meta: {
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            },
            children: [
              {
                path: "new",
                name: "adminUserGroupAdd",
                beforeEnter: async (
                  to: Route,
                  from: Route,
                  next: (options: boolean | Location | Location["path"]) => void
                ) => {
                  openUserGroupModal({
                    viewRouteName: "adminUserGroupAdd",
                    userGroupCode: to.params.userGroupCode,
                    params: to.params
                  });
                  store.dispatch("ui/endRouting");
                  return next(!from.matched.length);
                }
              },
              {
                path: `:userGroupCode`,
                name: "adminUserGroup",
                beforeEnter: async (
                  to: Route,
                  from: Route,
                  next: (options: boolean | Location | Location["path"]) => void
                ) => {
                  openUserGroupModal({
                    viewRouteName: "adminUserGroup",
                    userGroupCode: to.params.userGroupCode,
                    params: to.params
                  });
                  store.dispatch("ui/endRouting");
                  return next(!from.matched.length); // Important to maintain pagination/filters etc on parent list view
                }
              }
            ]
          }
        ]
      },
      {
        path: "api-tokens",
        redirect: { name: "adminApiTokens" },
        component: () => import("@/views/admin/settings/apiTokens/index.vue"),
        props: {
          routes: require("./menu").apiTokensTabigation
        },
        meta: {
          requiredFeatureKeys: [OrgFeatureKeys.CREATE_USER_API_TOKENS],
          auth: ["list_users"]
        },
        children: [
          {
            path: "all",
            name: "adminApiTokens",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: {
              id: "APITL1",
              isApiToken: true
            },
            meta: {
              title: "_.all_api_tokens",
              backTo: "_.all_api_tokens",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          },
          {
            path: "active",
            name: "adminApiTokensActive",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: {
              id: "APITL1",
              isApiToken: true,
              active: true,
              revoked: false
            },
            meta: {
              title: "_.active_api_tokens",
              backTo: "_.active_api_tokens",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          },
          {
            path: "inactive",
            name: "adminApiTokensInactive",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: {
              id: "APITL1",
              isApiToken: true,
              active: false,
              revoked: false
            },
            meta: {
              title: "_.inactive_api_tokens",
              backTo: "_.inactive_api_tokens",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          },
          {
            path: "revoked",
            name: "adminApiTokensRevoked",
            component: () =>
              import("@/components/app/admin/users/usersListing.vue"),
            props: {
              id: "APITL1",
              isApiToken: true,
              revoked: true
            },
            meta: {
              title: "_.revoked_api_tokens",
              backTo: "_.revoked_api_tokens",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["staff-users"]
            }
          }
        ]
      },
      {
        path: "client-auth-providers",
        name: AdminRoutes.CLIENT_AUTH_PROVIDERS,
        component: () =>
          import("@/views/admin/settings/clientAuthentication/index.vue"),
        meta: {
          auth: [],
          title: "_.client_auth_providers",
          backTo: "_.client_auth_providers",
          requiresBrand: true
        }
      },
      {
        path: `client-auth-providers/:configId(${RegexMatch.UUID})`,
        component: {
          name: "AdminClientAuthProviderConfig",
          template: `<router-view :key="$route.params.configId" />`
        },
        meta: {
          requiresBrand: true,
          auth: []
        },
        children: [
          {
            path: "",
            component: () =>
              import(
                "@/views/admin/settings/clientAuthentication/config/index.vue"
              ),
            props: (to: Route) => {
              return {
                routes: require("./menu").ClientAuthProviderTabigation,
                configId: to.params.configId
              };
            },
            children: [
              {
                path: "",
                redirect: "config"
              },
              {
                path: "config",
                name: AdminRoutes.CLIENT_AUTH_PROVIDER_CONFIG,
                component: () =>
                  import(
                    "@/views/admin/settings/clientAuthentication/config/edit/index.vue"
                  ),
                meta: {
                  title: "_.provider_configuration",
                  backTo: "_.provider_configuration"
                }
              },
              {
                path: "assignees",
                name: AdminRoutes.CLIENT_AUTH_PROVIDER_ASSIGNEES,
                component: () =>
                  import(
                    "@/views/admin/settings/clientAuthentication/config/actors/index.vue"
                  ),
                meta: {
                  title: "_.assigned_clients",
                  backTo: "_.assigned_clients"
                }
              },
              {
                path: "logs",
                name: AdminRoutes.CLIENT_AUTH_PROVIDER_LOGS,
                component: () =>
                  import(
                    "@/views/admin/settings/clientAuthentication/config/logs/index.vue"
                  ),
                meta: {
                  title: "_.provider_logs",
                  backTo: "_.provider_logs"
                }
              }
            ]
          }
        ]
      },
      {
        path: "staff-auth-providers",
        name: AdminRoutes.STAFF_AUTH_PROVIDERS,
        component: () =>
          import("@/views/admin/settings/userAuthentication/index.vue"),

        meta: {
          auth: [],
          title: "_.staff_auth_providers",
          backTo: "_.staff_auth_providers"
        }
      },
      {
        path: `staff-auth-providers/:configId(${RegexMatch.UUID})`,
        component: {
          name: "AdminStaffAuthProviderConfig",
          template: `<router-view :key="$route.params.configId" />`
        },
        meta: {
          auth: []
        },
        children: [
          {
            path: "",
            component: () =>
              import(
                "@/views/admin/settings/userAuthentication/config/index.vue"
              ),
            props: (to: Route) => {
              return {
                routes: require("./menu").StaffAuthProviderTabigation,
                configId: to.params.configId
              };
            },
            children: [
              {
                path: "",
                redirect: "config"
              },
              {
                path: "config",
                name: AdminRoutes.STAFF_AUTH_PROVIDER_CONFIG,
                component: () =>
                  import(
                    "@/views/admin/settings/userAuthentication/config/edit/index.vue"
                  ),
                meta: {
                  title: "_.provider_configuration",
                  backTo: "_.provider_configuration"
                }
              },
              {
                path: "assignees",
                name: AdminRoutes.STAFF_AUTH_PROVIDER_ASSIGNEES,
                component: () =>
                  import(
                    "@/views/admin/settings/userAuthentication/config/actors/index.vue"
                  ),
                meta: {
                  title: "_.assigned_staff",
                  backTo: "_.assigned_staff"
                }
              },
              {
                path: "logs",
                name: AdminRoutes.STAFF_AUTH_PROVIDER_LOGS,
                component: () =>
                  import(
                    "@/views/admin/settings/userAuthentication/config/logs/index.vue"
                  ),
                meta: {
                  title: "_.provider_logs",
                  backTo: "_.provider_logs"
                }
              }
            ]
          }
        ]
      },
      {
        path: "brands",
        name: "listAdminBrands",
        component: () => import("@/views/admin/settings/brands/index.vue"),
        meta: {
          auth: ["list_brands"],
          categorySlug: DocCategories.SETUP_CONFIGURATION,
          docsSlugs: ["branding.how-to-add-brands"]
        }
      },
      {
        path: "promotions",
        name: "admin__promotions",
        redirect: { name: "admin__allPromotions" },
        component: {
          name: "AdminPromotionsWrapper",
          template: "<router-view />"
        },
        meta: {
          requiresBrand: true
        },
        children: [
          {
            path: "list",
            name: "admin__listPromotions",
            component: AdminPromotionsView,
            props: {
              routes: AdminPromotionsRoutes
            },
            children: [
              {
                path: "all",
                name: "admin__allPromotions",
                component: PromotionsListing,
                props: {
                  availableSorters: () => [
                    ActiveSorter(),
                    ...DefaultPromotionsSorters()
                  ],
                  availableFilters: () => [
                    ActiveFilter(),
                    ...DefaultPromotionsFilters()
                  ]
                },
                meta: {
                  title: "_.all_promotions",
                  backTo: "_.all_promotions"
                }
              },
              {
                path: "active",
                name: "admin__activePromotions",
                component: PromotionsListing,
                props: {
                  availableSorters: DefaultPromotionsSorters,
                  availableFilters: DefaultPromotionsFilters,
                  routeFilters: () => ({
                    ...IsActiveParam()
                  })
                },
                meta: {
                  title: "_.active_promotions",
                  backTo: "_.active_promotions"
                }
              },
              {
                path: "inactive",
                name: "admin__inactivePromotions",
                component: PromotionsListing,
                props: {
                  availableSorters: DefaultPromotionsSorters,
                  availableFilters: DefaultPromotionsFilters,
                  routeFilters: () => ({
                    ...IsInactiveParam()
                  })
                },
                meta: {
                  title: "_.inactive_promotions",
                  backTo: "_.inactive_promotions"
                }
              },
              {
                path: "deleted",
                name: "admin__deletedPromotions",
                component: PromotionsListing,
                props: {
                  availableSorters: DefaultPromotionsSorters,
                  availableFilters: DefaultPromotionsFilters,
                  routeFilters: () => ({
                    ...IsDeletedPromotionParam()
                  })
                },
                meta: {
                  title: "_.deleted_promotions",
                  backTo: "_.deleted_promotions"
                }
              }
            ]
          },
          {
            path: "add",
            name: "admin__addPromotion",
            components: {
              default: () =>
                import("@/views/admin/settings/promotions/add/index.vue")
            },
            meta: {
              title: "_action.add_promotion",
              backTo: "_action.add_promotion",
              auth: [
                "create_promotion",
                "staff_list_products",
                "list_product_categories"
              ],
              breadcrumb: {
                label: "_.add"
              }
            },
            props: true
          },
          {
            path: `:promotionId(${RegexMatch.UUID})`,
            name: "admin__promotion",
            components: {
              default: () =>
                import("@/views/admin/settings/promotions/promotion/index.vue")
            },
            meta: {
              auth: ["get_promotion"],
              breadcrumb: {
                label: "_action.edit"
              }
            },
            props: {
              default: (route: Route) => ({
                promotionId: route.params.promotionId,
                routes: require("./menu").promotionTabigation
              })
            },
            redirect: { name: "adminPromotionConfiguration" },
            children: [
              {
                path: "configuration",
                name: "adminPromotionConfiguration",
                components: {
                  default: () =>
                    import(
                      "@/components/app/admin/catalogue/promotions/managePromotion.vue"
                    )
                },
                meta: {
                  backTo: "_.promotion_configuration",
                  title: "_.promotion_configuration",
                  breadcrumb: {
                    label: "_.configuration"
                  }
                }
              },
              {
                path: "uses",
                name: "adminPromotionUses",
                components: {
                  default: () =>
                    import(
                      "@/views/admin/settings/promotions/promotion/uses/index.vue"
                    )
                },
                meta: {
                  backTo: "_.promotion_uses",
                  title: "_.promotion_uses",
                  breadcrumb: {
                    label: "_.uses"
                  }
                }
              },
              {
                path: "activity-log",
                name: "adminPromotionActivity",
                components: {
                  default: () =>
                    import(
                      "@/views/admin/settings/promotions/promotion/activity/index.vue"
                    )
                },
                meta: {
                  backTo: "_.promotion_activity",
                  title: "_.promotion_activity",
                  breadcrumb: {
                    label: "_.activity_log"
                  }
                }
              }
            ]
          },
          {
            path: "*",
            component: () => import("@/views/error/index.vue"),
            props: { code: 404 }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue => admin/catalogue
      {
        path: "catalogue",
        redirect: { name: "adminProductCatalogue" },
        children: [
          {
            path: "products",
            redirect: { name: "adminProductCatalogue" }
          },
          {
            path: "options",
            redirect: { name: "adminCatalogueOptions" }
          },
          {
            path: "attributes",
            redirect: { name: "adminCatalogueAttributes" }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/product/:productId => admin/catalogue/product/:productId
      {
        path: `catalogue/product/:productId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueProductDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueProductDetails" }
          },
          {
            path: "billing",
            redirect: { name: "adminCatalogueProductBilling" }
          },
          {
            path: "automation",
            redirect: { name: "adminCatalogueProductAutomation" }
          },
          {
            path: "options",
            redirect: { name: "adminCatalogueProductOptions" }
          },
          {
            path: "attributes",
            redirect: { name: "adminCatalogueProductAttributes" }
          },
          {
            path: "recommendations",
            redirect: { name: "adminCatalogueProductRecommendations" }
          },
          {
            path: "provisioning",
            redirect: { name: "adminCatalogueProductProvisioning" }
          },

          {
            path: "notifications",
            redirect: { name: "adminCatalogueProductProvisioning" }
          },
          {
            path: "templates",
            redirect: { name: "admin__catalogueProductTemplateSlots" },
            children: [
              {
                path: "",
                redirect: { name: "admin__catalogueProductTemplateSlots" }
              },
              {
                path: "slots",
                redirect: { name: "admin__catalogueProductTemplateSlots" }
              },
              {
                path: "templates",
                redirect: { name: "admin__catalogueProductTemplateTemplates" }
              }
            ]
          },
          {
            path: "upgrade-downgrade",
            redirect: { name: "admin__catalogueProductUpgradeDowngrade" }
          },
          {
            path: "products",
            redirect: { name: "adminCatalogueProductContractProducts" },
            children: [
              {
                path: "all",
                redirect: { name: AdminRoutes.CATALOGUE_PRODUCT_ALL_CPS }
              },
              {
                path: "active",
                redirect: { name: AdminRoutes.CATALOGUE_PRODUCT_ACTIVE_CPS }
              },
              {
                path: "cancelled",
                redirect: { name: AdminRoutes.CATALOGUE_PRODUCT_CANCELLED_CPS }
              }
            ]
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/category/:categoryId => admin/catalogue/category/:categoryId
      {
        path: `catalogue/category/:categoryId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueCategoryDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueCategoryDetails" }
          },
          {
            path: "products",
            redirect: { name: "adminCatalogueCategoryProducts" }
          },
          {
            path: "provisioning",
            redirect: { name: "adminCatalogueCategoryProvisioning" }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/option/:productId => admin/catalogue/option/:productId
      {
        path: `catalogue/option/:productId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueOptionDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueOptionDetails" }
          },
          {
            path: "billing",
            redirect: { name: "adminCatalogueOptionBilling" }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/option-category/:categoryId => admin/catalogue/option-category/:categoryId
      {
        path: `catalogue/option-category/:categoryId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueOptionCategoryDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueOptionCategoryDetails" }
          },
          {
            path: "products",
            redirect: { name: "adminCatalogueOptionCategoryProducts" }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/attribute/:productId => admin/catalogue/attribute/:productId
      {
        path: `catalogue/attribute/:productId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueAttributeDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueAttributeDetails" }
          }
        ]
      },
      // Alias to new route admin/settings/catalogue/attribute-category/:categoryId => admin/catalogue/attribute-category/:categoryId
      {
        path: `catalogue/attribute-category/:categoryId(${RegexMatch.UUID})`,
        redirect: { name: "adminCatalogueAttributeCategoryDetails" },
        children: [
          {
            path: "details",
            redirect: { name: "adminCatalogueAttributeCategoryDetails" }
          },
          {
            path: "products",
            redirect: { name: "adminCatalogueAttributeCategoryProducts" }
          }
        ]
      },
      {
        path: "demo-data",
        name: AdminRoutes.DEMO_DATA_SETTINGS,
        component: () => import("@/views/admin/settings/demoData/index.vue"),
        meta: {
          requiresBrand: true,
          backTo: "_.seed_demo_data",
          title: "_.seed_demo_data"
        }
      },
      {
        path: "retention-reasons",
        name: AdminRoutes.SETTINGS_RETENTION_REASONS,
        component: () =>
          import("@/views/admin/settings/retentionReasons/index.vue"),
        meta: {
          auth: ["list_retention_reasons"],
          backTo: "_.retention_reasons",
          title: "_.retention_reasons"
        }
      },
      {
        path: `:settingId(${RegexMatch.SLUG})`,
        name: "adminDynamicSettings",
        component: () => import("@/views/admin/settings/dynamic/index.vue"),
        meta: {
          requiresBrand: true
        }
      },
      {
        path: "*",
        component: () => import("@/views/error/index.vue"),
        props: { code: 404 }
      }
    ]
  }
];
